/**
 * 空间页后端接口调用（类型见 SpaceType.ts，Mock 见 SpaceMock.ts）。
 */
import { post } from "../../utils/Backend";
import { useLongfansMock } from "../../global/GlobalMock";
import {
  mockGetSpaceHomeData,
  mockGetSpaceProfile,
  mockGetStartupConfig,
  mockGetSubscriptionDetail,
  mockGetSpaceFavorState,
  mockSetSpaceFavor,
} from "./SpaceMock";
import type {
  GetHomeDataResult,
  HomeLatestViewpoint,
  HomeLiveBroadcast,
  SpaceFavorState,
  SpaceModuleConfig,
  SpaceProfile,
  UserSubscriptionItem,
} from "./SpaceType";

function normalizeLive(
  live: HomeLiveBroadcast | null | undefined
): HomeLiveBroadcast | null {
  if (live == null) {
    return null;
  }
  return {
    coverUrl: live.coverUrl,
    liveId: live.liveId,
    title: live.title,
    phase: live.phase ?? "live",
    viewerCount: live.viewerCount ?? null,
    reserveCount: live.reserveCount ?? null,
  };
}

function normalizeViewpoint(
  vp: HomeLatestViewpoint | null | undefined
): HomeLatestViewpoint | null {
  if (vp == null) {
    return null;
  }
  return {
    imageUrl: vp.imageUrl,
    imageUrls: vp.imageUrls ?? null,
    content: vp.content,
    publishTime: vp.publishTime,
    contentAccessible: vp.contentAccessible ?? false,
    requiredProductDisplayName: vp.requiredProductDisplayName ?? null,
    spaceProductId: vp.spaceProductId ?? null,
    importance: vp.importance ?? null,
    categoryName: vp.categoryName ?? null,
    publisherLabel: vp.publisherLabel ?? null,
    publisherNickname: vp.publisherNickname ?? null,
    publisherAvatarUrl: vp.publisherAvatarUrl ?? null,
    title: vp.title ?? null,
    titleMode: vp.titleMode ?? null,
  };
}

const emptyHomeData = (): GetHomeDataResult => ({
  rotatingAds: [],
  live: null,
  latestViewpoint: null,
  courses: null,
});

export const SpaceApi = {
  async getSpaceProfile(spaceId: string): Promise<SpaceProfile | null> {
    if (useLongfansMock) {
      return mockGetSpaceProfile(spaceId);
    }
    const res = await post<{ spaceId: string }, SpaceProfile>(
      "/lark/app/space/getPublicSpaceProfile",
      { spaceId },
      { silent: true }
    );
    if (typeof res === "string") {
      return null;
    }
    return res;
  },

  async getStartupConfig(spaceId: string): Promise<SpaceModuleConfig[] | string> {
    if (useLongfansMock) {
      return mockGetStartupConfig(spaceId);
    }
    return post<{ spaceId: string }, SpaceModuleConfig[]>(
      "/lark/app/space/getStartupConfig",
      { spaceId },
      { silent: true }
    );
  },

  async getSpaceHomeData(spaceId: string): Promise<GetHomeDataResult> {
    if (useLongfansMock) {
      return mockGetSpaceHomeData(spaceId);
    }
    const res = await post<{ spaceId: string }, GetHomeDataResult>(
      "/lark/app/miniapp/home/getHomeData",
      { spaceId },
      { silent: true }
    );
    if (typeof res === "string") {
      return emptyHomeData();
    }
    if (res == null) {
      return emptyHomeData();
    }
    return {
      rotatingAds: res.rotatingAds ?? [],
      live: normalizeLive(res.live),
      latestViewpoint: normalizeViewpoint(res.latestViewpoint),
      courses: res.courses ?? null,
    };
  },

  async recordVideoTutorialPlay(
    spaceId: string,
    tutorialId: string
  ): Promise<number | null> {
    if (useLongfansMock) {
      return null;
    }
    const res = await post<
      { spaceId: string; tutorialId: string },
      { learnerCount?: number | null }
    >(
      "/lark/app/miniapp/home/recordVideoTutorialPlay",
      { spaceId, tutorialId },
      { silent: true }
    );
    if (typeof res === "string" || res == null) {
      return null;
    }
    return res.learnerCount ?? null;
  },

  async getSubscriptionDetail(
    spaceId: string
  ): Promise<UserSubscriptionItem[] | string> {
    if (useLongfansMock) {
      return mockGetSubscriptionDetail(spaceId);
    }
    return post<{ spaceId: string }, UserSubscriptionItem[]>(
      "/lark/app/user/getSubscriptionDetail",
      { spaceId },
      { silent: true }
    );
  },

  async getSpaceFavorState(spaceId: string): Promise<SpaceFavorState | null> {
    if (useLongfansMock) {
      return mockGetSpaceFavorState();
    }
    const res = await post<{ spaceId: string }, SpaceFavorState>(
      "/lark/app/user/getSpaceFavorState",
      { spaceId },
      { silent: true }
    );
    if (typeof res === "string" || res == null) {
      return null;
    }
    return res;
  },

  async setSpaceFavor(spaceId: string, favor: boolean): Promise<boolean> {
    if (useLongfansMock) {
      return mockSetSpaceFavor();
    }
    const res = await post<{ spaceId: string; favor: boolean }, boolean>(
      "/lark/app/user/setSpaceFavor",
      { spaceId, favor }
    );
    return typeof res !== "string";
  },
};
